import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Calendar, X } from 'lucide-react';

const DATE_OPTIONS = [
  'Today',
  'Yesterday',
  'Last 7 Days',
  'Last 30 Days',
  'Last Month',
  'All Time',
  'Custom Range',
];

const SADateFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setOpen(false);
        if (value !== 'Custom Range') setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value]);

  const handleOptionClick = (opt) => {
    if (opt === 'Custom Range') {
      setOpen(false);
      setShowCustomPicker(true);
      onChange(opt);
    } else {
      onChange(opt);
      setOpen(false);
      setShowCustomPicker(false);
      setCustomStart('');
      setCustomEnd('');
    }
  };

  const handleApplyCustom = () => {
    if (customStart && customEnd) {
      onChange(`${customStart} → ${customEnd}`);
      setShowCustomPicker(false);
    }
  };

  const handleClearCustom = () => {
    setCustomStart('');
    setCustomEnd('');
    onChange('Last 7 Days');
    setShowCustomPicker(false);
  };

  const displayLabel = value?.startsWith('20') || value?.includes('→')
    ? value
    : value;

  return (
    <div className="relative flex items-center gap-2" ref={containerRef}>
      {/* Custom date picker panel */}
      {showCustomPicker && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-30 p-4 w-72 max-w-[calc(100vw-2rem)]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-800">Custom Date Range</span>
            <button onClick={handleClearCustom} className="text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e?.target?.value)}
                max={customEnd || undefined}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e?.target?.value)}
                min={customStart || undefined}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleClearCustom}
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyCustom}
              disabled={!customStart || !customEnd}
              className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
            >
              Apply
            </button>
          </div>
        </div>
      )}
      {/* Main dropdown button */}
      <div className="relative">
        <button
          onClick={() => { setOpen(!open); setShowCustomPicker(false); }}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-gray-300 shadow-sm transition-colors"
        >
          <Calendar size={15} className="text-gray-400" />
          <span className="max-w-[160px] truncate">{displayLabel}</span>
          <ChevronDown size={14} className="text-gray-400" />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-20 overflow-hidden">
            {DATE_OPTIONS?.map((opt) => (
              <button
                key={opt}
                onClick={() => handleOptionClick(opt)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  (value === opt || (opt === 'Custom Range' && (value?.includes('→') || value === 'Custom Range')))
                    ? 'bg-blue-50 text-blue-600 font-semibold' :'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SADateFilter;
